import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  canonicalLevelColorScheme,
  jumpCourseSuperfix,
  type Assignment,
  type CompetitionDay,
  type Entry,
  type Event,
  type Jump,
  type JumpCourseLabelCandidate,
  type JumpGroup,
  type Level,
  type Shift,
  type Volunteer,
} from '@wht-xc-utils/shared/domain';
import {
  levelWindowFromEntries,
  shiftsCoverLevel,
  sortJudgingPosts,
  type LevelWindow,
} from '@wht-xc-utils/shared/scheduling';

import { EventTabs } from '../components/EventTabs';
import { JumpIndicators } from '../components/JumpIndicators';
import type { CoordinatorAssignmentSheet, PacketPdfPage } from '../components/PacketPdfDocument';
import { api } from '../lib/api';
import { addPacketMapImages, renderPostMapImage, type StaticPostMapPoint } from '../lib/packetMap';

interface PacketLevelRow {
  levelId: string;
  levelDisplayName: string;
  canonicalSlug: Level['canonicalSlug'];
  volunteerShiftNumber: number;
  jumps: Array<{
    jumpId: string;
    number: string;
    name: string | null;
    stoppingPoint: boolean;
    timingPoint: boolean;
    displayOrder: number;
  }>;
  riders: number;
  sheets: number;
}

interface PacketRow {
  id: string;
  assignmentIds: string[];
  jumpGroupIds: string[];
  volunteer: Volunteer;
  shadowVolunteerNames: string[];
  day: CompetitionDay;
  shifts: Shift[];
  rows: PacketLevelRow[];
  totalSheets: number;
  printOrder: PacketPrintOrder;
}

interface UnassignedPacketRow {
  id: string;
  jumpGroupId: string;
  day: CompetitionDay;
  volunteerShiftNumber: number;
  slotLabel: string;
  rows: PacketLevelRow[];
  totalSheets: number;
  printOrder: PacketPrintOrder;
}

interface CoordinatorAssignmentSheetRow extends CoordinatorAssignmentSheet {
  levelId: string;
  canonicalSlug: Level['canonicalSlug'];
  runOrder: number | null;
}

interface PacketPrintOrder {
  levelRunOrder: number;
  jumpDisplayOrder: number;
}

interface AutoAssignDaySummary {
  competitionDayId: string;
  created: number;
  postsStillUnfilled: number;
}

interface AutoAssignResultSummary {
  created: number;
  byDay: AutoAssignDaySummary[];
}

type CoverageStatus = 'full' | 'partial' | 'uncovered';

interface PostCoverageLevel {
  level: Level;
  jumps: Jump[];
  assignmentIds: string[];
}

interface PostCoverageShift {
  number: number;
  levels: PostCoverageLevel[];
  status: CoverageStatus;
}

interface PostCoverage {
  group: JumpGroup;
  displayNumber: number;
  levels: PostCoverageLevel[];
  shifts: PostCoverageShift[];
  status: CoverageStatus;
}

const JUMP_JUDGE_ROLE = 'XC Jump Judge';

function postJumpLabelCandidates(levels: PostCoverageLevel[]): JumpCourseLabelCandidate[] {
  return levels.flatMap((coverageLevel) =>
    coverageLevel.jumps.map((jump) => ({
      canonicalSlug: coverageLevel.level.canonicalSlug,
      number: jump.number,
      name: jump.name,
      latitude: jump.latitude,
      longitude: jump.longitude,
    })),
  );
}

function coverageStatus(covered: number, total: number): CoverageStatus {
  if (total > 0 && covered === total) return 'full';
  if (covered > 0) return 'partial';
  return 'uncovered';
}

function formatRideTime(mins: number): string {
  const hour = Math.floor(mins / 60);
  const minute = mins % 60;
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
}

function volunteerName(volunteer: Volunteer | undefined): string {
  if (!volunteer) return 'Unknown volunteer';
  const name = `${volunteer.firstName} ${volunteer.lastName}`.trim();
  return volunteer.experience === 'apprentice' ? `${name} (Apprentice)` : name;
}

function packetPdfFilename(day: CompetitionDay): string {
  const label = (day.label || day.date)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `woodside-jump-judge-packets-${label || 'day'}.pdf`;
}

function coordinatorPdfFilename(day: CompetitionDay): string {
  const label = (day.label || day.date)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `woodside-coordinator-assignments-${label || 'day'}.pdf`;
}

export function AssignmentsPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const queryClient = useQueryClient();

  const event = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => api.get<{ event: Event }>(`/api/events/${eventId}`),
    enabled: !!eventId,
  });
  const days = useQuery({
    queryKey: ['days', eventId],
    queryFn: () => api.get<{ days: CompetitionDay[] }>(`/api/events/${eventId}/days`),
    enabled: !!eventId,
  });
  const shifts = useQuery({
    queryKey: ['shifts', eventId],
    queryFn: () => api.get<{ shifts: Shift[] }>(`/api/events/${eventId}/shifts`),
    enabled: !!eventId,
  });
  const volunteers = useQuery({
    queryKey: ['volunteers', eventId],
    queryFn: () => api.get<{ volunteers: Volunteer[] }>(`/api/events/${eventId}/volunteers`),
    enabled: !!eventId,
  });
  const jumpGroups = useQuery({
    queryKey: ['jumpGroups', eventId],
    queryFn: () => api.get<{ jumpGroups: JumpGroup[] }>(`/api/events/${eventId}/jump-groups`),
    enabled: !!eventId,
  });
  const jumps = useQuery({
    queryKey: ['jumps', eventId],
    queryFn: () => api.get<{ jumps: Jump[] }>(`/api/events/${eventId}/jumps`),
    enabled: !!eventId,
  });
  const levels = useQuery({
    queryKey: ['levels', eventId],
    queryFn: () => api.get<{ levels: Level[] }>(`/api/events/${eventId}/levels`),
    enabled: !!eventId,
  });
  const entries = useQuery({
    queryKey: ['entries', eventId],
    queryFn: () => api.get<{ entries: Entry[] }>(`/api/events/${eventId}/entries`),
    enabled: !!eventId,
  });
  const assignments = useQuery({
    queryKey: ['assignments', eventId],
    queryFn: () => api.get<{ assignments: Assignment[] }>(`/api/events/${eventId}/assignments`),
    enabled: !!eventId,
  });
  const packets = useQuery({
    queryKey: ['packets', eventId],
    queryFn: () =>
      api.get<{
        packets: PacketRow[];
        unassigned: UnassignedPacketRow[];
        assignmentSheets: CoordinatorAssignmentSheetRow[];
      }>(`/api/events/${eventId}/packets`),
    enabled: !!eventId,
  });

  const sortedDays = useMemo(
    () => (days.data?.days ?? []).slice().sort((a, b) => a.dayOrder - b.dayOrder),
    [days.data],
  );
  const volunteerById = useMemo(
    () => new Map((volunteers.data?.volunteers ?? []).map((item) => [item.id, item])),
    [volunteers.data],
  );
  const jumpById = useMemo(
    () => new Map((jumps.data?.jumps ?? []).map((item) => [item.id, item])),
    [jumps.data],
  );
  const levelById = useMemo(
    () => new Map((levels.data?.levels ?? []).map((item) => [item.id, item])),
    [levels.data],
  );
  const groupById = useMemo(
    () => new Map((jumpGroups.data?.jumpGroups ?? []).map((item) => [item.id, item])),
    [jumpGroups.data],
  );

  const judgeShiftsByVolunteerDay = useMemo(() => {
    const result = new Map<string, Shift[]>();
    for (const shift of shifts.data?.shifts ?? []) {
      if (shift.role !== JUMP_JUDGE_ROLE || shift.competitionDayId == null) {
        continue;
      }
      const key = `${shift.volunteerId}::${shift.competitionDayId}`;
      const list = result.get(key) ?? [];
      list.push(shift);
      result.set(key, list);
    }
    return result;
  }, [shifts.data]);

  const levelWindowById = useMemo(() => {
    const entriesByLevel = new Map<string, Entry[]>();
    for (const entry of entries.data?.entries ?? []) {
      if (!entry.levelId) continue;
      const list = entriesByLevel.get(entry.levelId) ?? [];
      list.push(entry);
      entriesByLevel.set(entry.levelId, list);
    }
    return new Map<string, LevelWindow | null>(
      (levels.data?.levels ?? []).map((level) => [
        level.id,
        levelWindowFromEntries(entriesByLevel.get(level.id) ?? [], { paddingMin: 0 }),
      ]),
    );
  }, [entries.data, levels.data]);

  const effectiveLevelsByAssignment = useMemo(() => {
    const result = new Map<string, Set<string>>();
    for (const assignment of assignments.data?.assignments ?? []) {
      if (assignment.levelIds.length > 0) {
        result.set(assignment.id, new Set(assignment.levelIds));
        continue;
      }
      const group = groupById.get(assignment.jumpGroupId);
      const volunteerShifts =
        judgeShiftsByVolunteerDay.get(
          `${assignment.volunteerId}::${assignment.competitionDayId}`,
        ) ?? [];
      const effective = new Set<string>();
      for (const jumpId of group?.jumpIds ?? []) {
        const jump = jumpById.get(jumpId);
        if (!jump || effective.has(jump.levelId)) continue;
        const window = levelWindowById.get(jump.levelId);
        if (window && shiftsCoverLevel(volunteerShifts, window)) {
          effective.add(jump.levelId);
        }
      }
      result.set(assignment.id, effective);
    }
    return result;
  }, [assignments.data, groupById, judgeShiftsByVolunteerDay, jumpById, levelWindowById]);

  const [selectedDayId, setSelectedDayId] = useState('');
  const [volunteerSelections, setVolunteerSelections] = useState<Record<string, string>>({});
  const [writeInNames, setWriteInNames] = useState<Record<string, string>>({});
  const [packetPdfBuilding, setPacketPdfBuilding] = useState(false);
  const [packetPdfError, setPacketPdfError] = useState<string | null>(null);
  const [coordinatorPdfBuilding, setCoordinatorPdfBuilding] = useState(false);
  const [coordinatorPdfError, setCoordinatorPdfError] = useState<string | null>(null);
  const [autoAssignSummary, setAutoAssignSummary] = useState<AutoAssignResultSummary | null>(null);

  useEffect(() => {
    if (!sortedDays.some((day) => day.id === selectedDayId)) {
      setSelectedDayId(sortedDays[0]?.id ?? '');
    }
  }, [selectedDayId, sortedDays]);

  const selectedDay = sortedDays.find((day) => day.id === selectedDayId) ?? null;

  const postCoverage = useMemo<PostCoverage[]>(() => {
    if (!selectedDay) return [];
    const dayAssignments = (assignments.data?.assignments ?? []).filter(
      (assignment) => assignment.competitionDayId === selectedDay.id,
    );
    const groups = sortJudgingPosts(
      (jumpGroups.data?.jumpGroups ?? []).filter(
        (group) => group.competitionDayId === selectedDay.id,
      ),
      jumps.data?.jumps ?? [],
      levels.data?.levels ?? [],
    );
    return groups.map((group, index) => {
      const jumpsByLevel = new Map<string, Jump[]>();
      for (const jumpId of group.jumpIds) {
        const jump = jumpById.get(jumpId);
        if (!jump) continue;
        const list = jumpsByLevel.get(jump.levelId) ?? [];
        list.push(jump);
        jumpsByLevel.set(jump.levelId, list);
      }
      const coverageLevels = [...jumpsByLevel.entries()]
        .flatMap(([levelId, groupJumps]): PostCoverageLevel[] => {
          const level = levelById.get(levelId);
          if (!level) return [];
          return [
            {
              level,
              jumps: groupJumps.slice().sort((a, b) => a.displayOrder - b.displayOrder),
              assignmentIds: dayAssignments
                .filter(
                  (assignment) =>
                    assignment.jumpGroupId === group.id &&
                    effectiveLevelsByAssignment.get(assignment.id)?.has(level.id),
                )
                .map((assignment) => assignment.id),
            },
          ];
        })
        .sort((a, b) => (a.level.runOrder ?? 9999) - (b.level.runOrder ?? 9999));
      const levelsByShift = new Map<number, PostCoverageLevel[]>();
      for (const level of coverageLevels) {
        const list = levelsByShift.get(level.level.volunteerShiftNumber) ?? [];
        list.push(level);
        levelsByShift.set(level.level.volunteerShiftNumber, list);
      }
      const postShifts = [...levelsByShift.entries()]
        .sort(([a], [b]) => a - b)
        .map(([number, shiftLevels]) => ({
          number,
          levels: shiftLevels,
          status: coverageStatus(
            shiftLevels.filter((level) => level.assignmentIds.length > 0).length,
            shiftLevels.length,
          ),
        }));
      return {
        group,
        displayNumber: index + 1,
        levels: coverageLevels,
        shifts: postShifts,
        status: coverageStatus(
          coverageLevels.filter((level) => level.assignmentIds.length > 0).length,
          coverageLevels.length,
        ),
      };
    });
  }, [
    assignments.data,
    effectiveLevelsByAssignment,
    jumpById,
    jumpGroups.data,
    jumps.data,
    levelById,
    levels.data,
    selectedDay,
  ]);

  const selectedDayVolunteers = useMemo(() => {
    if (!selectedDay) return [];
    return (volunteers.data?.volunteers ?? [])
      .filter((volunteer) => judgeShiftsByVolunteerDay.has(`${volunteer.id}::${selectedDay.id}`))
      .slice()
      .sort(
        (a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName),
      );
  }, [judgeShiftsByVolunteerDay, selectedDay, volunteers.data]);

  const assignableLevels = (volunteerId: string, shift: PostCoverageShift): PostCoverageLevel[] => {
    if (!selectedDay) return [];
    const volunteer = volunteerById.get(volunteerId);
    const volunteerShifts =
      judgeShiftsByVolunteerDay.get(`${volunteerId}::${selectedDay.id}`) ?? [];
    if (volunteer?.experience === 'apprentice') {
      return shift.levels.filter((coverageLevel) => {
        const window = levelWindowById.get(coverageLevel.level.id);
        if (!window || !shiftsCoverLevel(volunteerShifts, window)) return false;
        return coverageLevel.assignmentIds.some((assignmentId) => {
          const assignment = assignments.data?.assignments.find((item) => item.id === assignmentId);
          const partner = assignment ? volunteerById.get(assignment.volunteerId) : undefined;
          return partner && partner.experience !== 'novice' && partner.experience !== 'apprentice';
        });
      });
    }
    if (shift.levels.some((coverageLevel) => coverageLevel.assignmentIds.length > 0)) {
      return [];
    }
    if (
      volunteer?.experience === 'novice' &&
      shift.levels.some((coverageLevel) =>
        coverageLevel.jumps.some(
          (jump) => jump.difficultToJudge || jump.stoppingPoint || jump.timingPoint,
        ),
      )
    ) {
      return [];
    }
    const coversWholeShift = shift.levels.every((coverageLevel) => {
      const window = levelWindowById.get(coverageLevel.level.id);
      return window != null && shiftsCoverLevel(volunteerShifts, window);
    });
    return coversWholeShift ? shift.levels : [];
  };

  const isDoubleBookedForShift = (
    volunteerId: string,
    competitionDayId: string,
    volunteerShiftNumber: number,
    jumpGroupId: string,
  ): boolean =>
    (assignments.data?.assignments ?? []).some(
      (assignment) =>
        assignment.volunteerId === volunteerId &&
        assignment.competitionDayId === competitionDayId &&
        assignment.jumpGroupId !== jumpGroupId &&
        [...(effectiveLevelsByAssignment.get(assignment.id) ?? [])].some(
          (levelId) => levelById.get(levelId)?.volunteerShiftNumber === volunteerShiftNumber,
        ),
    );

  const invalidateCoverage = (): void => {
    queryClient.invalidateQueries({ queryKey: ['assignments', eventId] });
    queryClient.invalidateQueries({ queryKey: ['packets', eventId] });
  };

  const saveCoverage = useMutation({
    mutationFn: async (input: {
      volunteerId: string;
      jumpGroupId: string;
      competitionDayId: string;
      levelIds: string[];
    }) => {
      const existing = (assignments.data?.assignments ?? []).find(
        (assignment) =>
          assignment.volunteerId === input.volunteerId &&
          assignment.jumpGroupId === input.jumpGroupId,
      );
      if (!existing) {
        return api.post(`/api/events/${eventId}/assignments`, input);
      }
      const combined = new Set(effectiveLevelsByAssignment.get(existing.id) ?? existing.levelIds);
      for (const levelId of input.levelIds) combined.add(levelId);
      return api.patch(`/api/events/${eventId}/assignments/${existing.id}`, {
        levelIds: [...combined],
      });
    },
    onSuccess: (_data, input) => {
      invalidateCoverage();
      setVolunteerSelections((current) => {
        const next = { ...current };
        for (const key of Object.keys(next)) {
          if (key.startsWith(`${input.jumpGroupId}::`)) delete next[key];
        }
        return next;
      });
    },
  });

  const saveWriteInCoverage = useMutation({
    mutationFn: (input: {
      selectionKey: string;
      name: string;
      jumpGroupId: string;
      competitionDayId: string;
      levelIds: string[];
    }) =>
      api.post(`/api/events/${eventId}/assignments/write-in`, {
        name: input.name,
        jumpGroupId: input.jumpGroupId,
        competitionDayId: input.competitionDayId,
        levelIds: input.levelIds,
      }),
    onSuccess: (_data, input) => {
      invalidateCoverage();
      queryClient.invalidateQueries({ queryKey: ['volunteers', eventId] });
      setWriteInNames((current) => {
        const next = { ...current };
        delete next[input.selectionKey];
        return next;
      });
    },
  });

  const removeCoverage = useMutation({
    mutationFn: async (input: { assignmentId: string; levelIds: string[] }) => {
      const assignment = (assignments.data?.assignments ?? []).find(
        (item) => item.id === input.assignmentId,
      );
      if (!assignment) throw new Error('Assignment not found');
      const remaining = new Set(
        effectiveLevelsByAssignment.get(assignment.id) ?? assignment.levelIds,
      );
      for (const levelId of input.levelIds) remaining.delete(levelId);
      if (remaining.size === 0) {
        return api.delete(`/api/events/${eventId}/assignments/${assignment.id}`);
      }
      return api.patch(`/api/events/${eventId}/assignments/${assignment.id}`, {
        levelIds: [...remaining],
      });
    },
    onSuccess: invalidateCoverage,
  });

  const autoAssign = useMutation({
    mutationFn: (competitionDayId: string) =>
      api.post<AutoAssignResultSummary>(`/api/events/${eventId}/assignments/auto-assign`, {
        competitionDayId,
      }),
    onSuccess: (summary) => {
      setAutoAssignSummary(summary);
      invalidateCoverage();
    },
  });

  const clearDayAssignments = useMutation({
    mutationFn: (competitionDayId: string) =>
      api.delete<{ deleted: number }>(
        `/api/events/${eventId}/assignments/day/${competitionDayId}`,
      ),
    onSuccess: () => {
      setAutoAssignSummary(null);
      invalidateCoverage();
    },
  });

  const packetPdfPages = useMemo<PacketPdfPage[]>(() => {
    if (!selectedDay) return [];
    const schedule = (levels.data?.levels ?? [])
      .filter((level) => level.competitionDayId === selectedDay.id)
      .slice()
      .sort((a, b) => (a.runOrder ?? 9999) - (b.runOrder ?? 9999))
      .map((level) => {
        const window = levelWindowFromEntries(
          (entries.data?.entries ?? []).filter((entry) => entry.levelId === level.id),
          { paddingMin: 0 },
        );
        return {
          levelDisplayName: level.displayName,
          firstRideTime: window == null ? null : formatRideTime(window.startMin),
        };
      });
    const pdfRows = (rows: PacketLevelRow[]) =>
      rows.map((row) => ({
        ...row,
        jumps: row.jumps.map((jump) => {
          const source = jumpById.get(jump.jumpId);
          return {
            ...jump,
            stoppingPoint: source?.stoppingPoint ?? jump.stoppingPoint,
            timingPoint: source?.timingPoint ?? jump.timingPoint,
            latitude: source?.latitude ?? null,
            longitude: source?.longitude ?? null,
          };
        }),
      }));
    const assignedPages = (packets.data?.packets ?? [])
      .filter((packet) => packet.day.id === selectedDay.id && packet.rows.length > 0)
      .map((packet) => ({
        printOrder: packet.printOrder,
        page: {
          id: packet.id,
          day: packet.day,
          volunteerName: volunteerName(packet.volunteer),
          shadowNames: packet.shadowVolunteerNames,
          schedule,
          rows: pdfRows(packet.rows),
          totalSheets: packet.totalSheets,
        },
      }));
    const unassignedPages = (packets.data?.unassigned ?? [])
      .filter((packet) => packet.day.id === selectedDay.id)
      .map((packet) => ({
        printOrder: packet.printOrder,
        page: {
          id: packet.id,
          day: packet.day,
          volunteerName: null,
          shadowNames: [],
          schedule,
          rows: pdfRows(packet.rows),
          totalSheets: packet.totalSheets,
        },
      }));
    return [...assignedPages, ...unassignedPages]
      .sort(
        (a, b) =>
          a.printOrder.levelRunOrder - b.printOrder.levelRunOrder ||
          a.printOrder.jumpDisplayOrder - b.printOrder.jumpDisplayOrder ||
          a.page.id.localeCompare(b.page.id),
      )
      .map((item) => item.page);
  }, [entries.data, jumpById, levels.data, packets.data, selectedDay]);
  const coordinatorAssignmentSheets = useMemo<CoordinatorAssignmentSheet[]>(
    () =>
      (packets.data?.assignmentSheets ?? [])
        .filter((sheet) => sheet.day.id === selectedDay?.id)
        .sort((a, b) => (a.runOrder ?? 9999) - (b.runOrder ?? 9999)),
    [packets.data, selectedDay],
  );

  const downloadPacketPdf = async () => {
    if (
      !selectedDay ||
      packetPdfPages.length === 0 ||
      jumps.isLoading ||
      entries.isLoading ||
      event.isLoading
    ) {
      return;
    }
    setPacketPdfBuilding(true);
    setPacketPdfError(null);
    try {
      const [{ pdf }, { PacketPdfDocument }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('../components/PacketPdfDocument'),
      ]);
      const pagesWithMaps = await addPacketMapImages(packetPdfPages);
      const blob = await pdf(
        <PacketPdfDocument
          pages={pagesWithMaps}
          coordinators={event.data?.event.coordinators ?? []}
          instructions={event.data?.event.packetInstructions ?? []}
          stoppingPointInstructions={event.data?.event.stoppingPointInstructions ?? []}
          timingPointInstructions={event.data?.event.timingPointInstructions ?? []}
        />,
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = packetPdfFilename(selectedDay);
      document.body.append(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (error) {
      setPacketPdfError(
        error instanceof Error ? error.message : 'Could not generate the packet PDF.',
      );
    } finally {
      setPacketPdfBuilding(false);
    }
  };

  const downloadCoordinatorPdf = async () => {
    if (!selectedDay || coordinatorAssignmentSheets.length === 0) return;
    setCoordinatorPdfBuilding(true);
    setCoordinatorPdfError(null);
    try {
      const [{ pdf }, { CoordinatorAssignmentDocument }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('../components/PacketPdfDocument'),
      ]);
      const blob = await pdf(
        <CoordinatorAssignmentDocument sheets={coordinatorAssignmentSheets} />,
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = coordinatorPdfFilename(selectedDay);
      document.body.append(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (error) {
      setCoordinatorPdfError(
        error instanceof Error ? error.message : 'Could not generate coordinator sheets.',
      );
    } finally {
      setCoordinatorPdfBuilding(false);
    }
  };

  const summary = {
    full: postCoverage.filter((post) => post.status === 'full').length,
    partial: postCoverage.filter((post) => post.status === 'partial').length,
    uncovered: postCoverage.filter((post) => post.status === 'uncovered').length,
  };
  const selectedDayAssignmentCount = (assignments.data?.assignments ?? []).filter(
    (assignment) => assignment.competitionDayId === selectedDay?.id,
  ).length;
  const mutationError =
    saveCoverage.error ??
    saveWriteInCoverage.error ??
    removeCoverage.error ??
    autoAssign.error ??
    clearDayAssignments.error;

  return (
    <>
      <EventTabs />

      <section className="card coverage-workspace">
        <div className="coverage-heading">
          <div>
            <span className="eyebrow">Judge post coverage</span>
            <h2>Assignments</h2>
            <p className="muted">
              Work through each judging post by numbered volunteer shift. Assignments cover only the
              listed levels and can be shared across sequential shifts.
            </p>
          </div>
          <div className="coverage-day-actions">
            <label>
              Competition day
              <select
                value={selectedDay?.id ?? ''}
                onChange={(event) => setSelectedDayId(event.target.value)}
              >
                {sortedDays.map((day) => (
                  <option key={day.id} value={day.id}>
                    {day.label || day.date}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="subtle"
              disabled={!selectedDay || autoAssign.isPending || clearDayAssignments.isPending}
              onClick={() => selectedDay && autoAssign.mutate(selectedDay.id)}
            >
              {autoAssign.isPending ? 'Auto-assigning…' : 'Auto-assign open posts'}
            </button>
            <button
              className="subtle danger"
              disabled={
                !selectedDay ||
                selectedDayAssignmentCount === 0 ||
                autoAssign.isPending ||
                clearDayAssignments.isPending
              }
              onClick={() => {
                if (!selectedDay) return;
                const dayLabel = selectedDay.label || selectedDay.date;
                if (
                  confirm(
                    `Remove all ${selectedDayAssignmentCount} volunteer assignment${
                      selectedDayAssignmentCount === 1 ? '' : 's'
                    } for ${dayLabel}?`,
                  )
                ) {
                  clearDayAssignments.mutate(selectedDay.id);
                }
              }}
            >
              {clearDayAssignments.isPending ? 'Removing…' : 'Remove all assignments'}
            </button>
          </div>
        </div>

        <div className="coverage-summary">
          <div>
            <strong>{summary.full}</strong>
            <span>Fully covered</span>
          </div>
          <div>
            <strong>{summary.partial}</strong>
            <span>Partially covered</span>
          </div>
          <div>
            <strong>{summary.uncovered}</strong>
            <span>Uncovered</span>
          </div>
        </div>

        {autoAssignSummary ? (
          <p className="muted coverage-notice">
            Auto-assignment created {autoAssignSummary.created} assignment
            {autoAssignSummary.created === 1 ? '' : 's'}.
          </p>
        ) : null}
        {clearDayAssignments.data ? (
          <p className="muted coverage-notice">
            Removed {clearDayAssignments.data.deleted} assignment
            {clearDayAssignments.data.deleted === 1 ? '' : 's'}.
          </p>
        ) : null}
        {mutationError ? (
          <p className="error coverage-notice">{(mutationError as Error).message}</p>
        ) : null}

        {postCoverage.length === 0 ? (
          <p className="muted">
            No judging posts are configured for this day. Create them on the Jumps tab first.
          </p>
        ) : (
          <div className="post-coverage-list">
            {postCoverage.map((post) => (
              <article className="post-coverage-card" key={post.group.id}>
                <header className="post-coverage-header">
                  <div>
                    <div className="post-coverage-title">
                      <h3>Judging post {post.displayNumber}</h3>
                      <CoveragePill status={post.status} />
                    </div>
                  </div>
                </header>

                <div className="post-coverage-body">
                  <div className="post-shift-list">
                    {post.shifts.map((postShift) => {
                      const selectionKey = `${post.group.id}::${postShift.number}`;
                      const selectedVolunteerId = volunteerSelections[selectionKey] ?? '';
                      const targetLevels = selectedVolunteerId
                        ? assignableLevels(selectedVolunteerId, postShift)
                        : [];
                      const writeInName = writeInNames[selectionKey] ?? '';
                      const writeInTargetLevels = postShift.levels.filter(
                        (coverageLevel) => coverageLevel.assignmentIds.length === 0,
                      );
                      const eligibleVolunteers = selectedDayVolunteers.filter(
                        (volunteer) => assignableLevels(volunteer.id, postShift).length > 0,
                      );
                      const conflictFreeVolunteers = eligibleVolunteers.filter(
                        (volunteer) =>
                          !selectedDay ||
                          !isDoubleBookedForShift(
                            volunteer.id,
                            selectedDay.id,
                            postShift.number,
                            post.group.id,
                          ),
                      );
                      const conflictingVolunteers = eligibleVolunteers.filter(
                        (volunteer) =>
                          !!selectedDay &&
                          isDoubleBookedForShift(
                            volunteer.id,
                            selectedDay.id,
                            postShift.number,
                            post.group.id,
                          ),
                      );
                      const shiftAssignmentIds = [
                        ...new Set(
                          postShift.levels.flatMap((coverageLevel) => coverageLevel.assignmentIds),
                        ),
                      ];
                      return (
                        <section className="post-shift" key={postShift.number}>
                          <div className="post-shift-heading">
                            <h4>Shift {postShift.number}</h4>
                          </div>

                          <div className="post-level-coverage">
                            {postShift.levels.map((coverageLevel) => {
                              const colors = canonicalLevelColorScheme(
                                coverageLevel.level.canonicalSlug,
                              );
                              const assignees = coverageLevel.assignmentIds.map((assignmentId) => {
                                const assignment = assignments.data?.assignments.find(
                                  (item) => item.id === assignmentId,
                                );
                                return {
                                  name: volunteerName(
                                    assignment
                                      ? volunteerById.get(assignment.volunteerId)
                                      : undefined,
                                  ),
                                  doubleBooked:
                                    !!assignment &&
                                    isDoubleBookedForShift(
                                      assignment.volunteerId,
                                      assignment.competitionDayId,
                                      postShift.number,
                                      post.group.id,
                                    ),
                                };
                              });
                              const labelCandidates = postJumpLabelCandidates(post.levels);
                              return (
                                <div className="post-level-row" key={coverageLevel.level.id}>
                                  <span
                                    className="post-level-chip"
                                    style={{
                                      color: colors.textColor,
                                      background: colors.backgroundColor,
                                    }}
                                  >
                                    <span>{coverageLevel.level.displayName} </span>
                                    {coverageLevel.jumps.map((jump, index) => {
                                      const superfix = jumpCourseSuperfix(
                                        {
                                          canonicalSlug: coverageLevel.level.canonicalSlug,
                                          number: jump.number,
                                          name: jump.name,
                                          latitude: jump.latitude,
                                          longitude: jump.longitude,
                                        },
                                        labelCandidates,
                                      );
                                      return (
                                        <span key={jump.id} className="jump-label-with-indicators">
                                          {index > 0 ? ', ' : ''}
                                          {jump.number}
                                          {superfix ? <sup>{superfix}</sup> : null}
                                          {jump.name ? ` · ${jump.name}` : ''}
                                          <JumpIndicators jump={jump} />
                                        </span>
                                      );
                                    })}
                                  </span>
                                  <span
                                    className={
                                      assignees.length > 0
                                        ? 'post-level-assignee'
                                        : 'post-level-open'
                                    }
                                  >
                                    {assignees.length > 0
                                      ? assignees
                                          .map(
                                            (assignee) =>
                                              `${assignee.name}${
                                                assignee.doubleBooked ? ' ⚠' : ''
                                              }`,
                                          )
                                          .join(', ')
                                      : 'Needs coverage'}
                                  </span>
                                </div>
                              );
                            })}
                          </div>

                          {shiftAssignmentIds.length > 0 ? (
                            <div className="shift-assignees">
                              {shiftAssignmentIds.map((assignmentId) => {
                                const assignment = assignments.data?.assignments.find(
                                  (item) => item.id === assignmentId,
                                );
                                if (!assignment) return null;
                                const coveredLevels = postShift.levels.filter((coverageLevel) =>
                                  coverageLevel.assignmentIds.includes(assignment.id),
                                );
                                const doubleBooked = isDoubleBookedForShift(
                                  assignment.volunteerId,
                                  assignment.competitionDayId,
                                  postShift.number,
                                  post.group.id,
                                );
                                return (
                                  <span className="shift-assignee" key={assignment.id}>
                                    <span>
                                      <strong>
                                        {volunteerName(volunteerById.get(assignment.volunteerId))}
                                        {doubleBooked ? (
                                          <span
                                            className="assignment-warning"
                                            title={`Also assigned to another post during shift ${postShift.number}`}
                                          >
                                            {' '}
                                            ⚠
                                          </span>
                                        ) : null}
                                      </strong>
                                      <small>
                                        {coveredLevels
                                          .map((coverageLevel) => coverageLevel.level.displayName)
                                          .join(', ')}
                                      </small>
                                    </span>
                                    <button
                                      className="subtle danger icon-button"
                                      aria-label={`Remove ${volunteerName(
                                        volunteerById.get(assignment.volunteerId),
                                      )} from shift ${postShift.number}`}
                                      disabled={removeCoverage.isPending}
                                      onClick={() =>
                                        removeCoverage.mutate({
                                          assignmentId: assignment.id,
                                          levelIds: coveredLevels.map(
                                            (coverageLevel) => coverageLevel.level.id,
                                          ),
                                        })
                                      }
                                    >
                                      ×
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                          ) : null}

                          {postShift.status !== 'full' ||
                          eligibleVolunteers.some(
                            (volunteer) => volunteer.experience === 'apprentice',
                          ) ? (
                            <div className="shift-assignment-controls">
                              <div className="existing-volunteer-assignment">
                                {eligibleVolunteers.length > 0 ? (
                                  <>
                                    <select
                                      value={selectedVolunteerId}
                                      onChange={(event) =>
                                        setVolunteerSelections((current) => ({
                                          ...current,
                                          [selectionKey]: event.target.value,
                                        }))
                                      }
                                    >
                                      <option value="">Assign a volunteer…</option>
                                      {conflictFreeVolunteers.length > 0 ? (
                                        <optgroup label="Available volunteers">
                                          {conflictFreeVolunteers.map((volunteer) => (
                                            <VolunteerOption
                                              key={volunteer.id}
                                              volunteer={volunteer}
                                            />
                                          ))}
                                        </optgroup>
                                      ) : null}
                                      {conflictingVolunteers.length > 0 ? (
                                        <optgroup label="Overlapping assignment — assign with care">
                                          {conflictingVolunteers.map((volunteer) => (
                                            <VolunteerOption
                                              key={volunteer.id}
                                              volunteer={volunteer}
                                            />
                                          ))}
                                        </optgroup>
                                      ) : null}
                                    </select>
                                    <button
                                      className="primary"
                                      disabled={
                                        targetLevels.length === 0 ||
                                        saveCoverage.isPending ||
                                        !selectedDay
                                      }
                                      onClick={() =>
                                        selectedDay &&
                                        saveCoverage.mutate({
                                          volunteerId: selectedVolunteerId,
                                          jumpGroupId: post.group.id,
                                          competitionDayId: selectedDay.id,
                                          levelIds: targetLevels.map(
                                            (coverageLevel) => coverageLevel.level.id,
                                          ),
                                        })
                                      }
                                    >
                                      {saveCoverage.isPending
                                        ? 'Assigning…'
                                        : targetLevels.length > 0
                                          ? `Assign ${targetLevels.length} level${
                                              targetLevels.length === 1 ? '' : 's'
                                            }`
                                          : 'Assign'}
                                    </button>
                                  </>
                                ) : (
                                  <span className="muted">
                                    No signed-up volunteers overlap the uncovered levels.
                                  </span>
                                )}
                              </div>
                              <div className="write-in-assignment">
                                <input
                                  type="text"
                                  placeholder="Write-in volunteer name"
                                  value={writeInName}
                                  onChange={(event) =>
                                    setWriteInNames((current) => ({
                                      ...current,
                                      [selectionKey]: event.target.value,
                                    }))
                                  }
                                />
                                <button
                                  className="subtle"
                                  disabled={
                                    !selectedDay ||
                                    !writeInName.trim() ||
                                    writeInTargetLevels.length === 0 ||
                                    saveWriteInCoverage.isPending
                                  }
                                  onClick={() =>
                                    selectedDay &&
                                    saveWriteInCoverage.mutate({
                                      selectionKey,
                                      name: writeInName.trim(),
                                      jumpGroupId: post.group.id,
                                      competitionDayId: selectedDay.id,
                                      levelIds: writeInTargetLevels.map(
                                        (coverageLevel) => coverageLevel.level.id,
                                      ),
                                    })
                                  }
                                >
                                  {saveWriteInCoverage.isPending ? 'Adding…' : 'Write in & assign'}
                                </button>
                              </div>
                            </div>
                          ) : null}
                        </section>
                      );
                    })}
                  </div>
                  <PostCoverageMap post={post} />
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="card packet-download-card">
        <div>
          <span className="eyebrow">Day-of output</span>
          <h2>Download coordinator and packet sheets</h2>
          <p className="muted">
            Generate a coordinator assignment PDF for every level or a separate PDF containing the
            assigned and still-open judge packets for{' '}
            {selectedDay?.label || selectedDay?.date || 'the selected day'}.
          </p>
        </div>
        <div className="packet-download-actions">
          <button
            className="subtle"
            disabled={
              !selectedDay || coordinatorAssignmentSheets.length === 0 || coordinatorPdfBuilding
            }
            onClick={downloadCoordinatorPdf}
          >
            {coordinatorPdfBuilding
              ? 'Building coordinator PDF…'
              : coordinatorAssignmentSheets.length > 0
                ? `Download ${coordinatorAssignmentSheets.length} coordinator sheet${
                    coordinatorAssignmentSheets.length === 1 ? '' : 's'
                  }`
                : 'No coordinator sheets'}
          </button>
          <button
            className="primary"
            disabled={
              !selectedDay ||
              packetPdfPages.length === 0 ||
              packetPdfBuilding ||
              jumps.isLoading ||
              entries.isLoading ||
              event.isLoading
            }
            onClick={downloadPacketPdf}
          >
            {packetPdfBuilding
              ? 'Building packet PDF…'
              : packetPdfPages.length > 0
                ? `Download ${packetPdfPages.length} packet sheet${
                    packetPdfPages.length === 1 ? '' : 's'
                  }`
                : 'No packet sheets'}
          </button>
          {packetPdfError ? <span className="error">{packetPdfError}</span> : null}
          {coordinatorPdfError ? <span className="error">{coordinatorPdfError}</span> : null}
        </div>
      </section>
    </>
  );
}

function PostCoverageMap({ post }: { post: PostCoverage }) {
  const [image, setImage] = useState<string | null | undefined>(undefined);
  const [mapSize, setMapSize] = useState<{ width: number; height: number } | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const points = useMemo<StaticPostMapPoint[]>(() => {
    const labelCandidates = postJumpLabelCandidates(post.levels);
    return post.levels.flatMap((coverageLevel) => {
      const colors = canonicalLevelColorScheme(coverageLevel.level.canonicalSlug);
      return coverageLevel.jumps.flatMap((jump): StaticPostMapPoint[] =>
        jump.latitude == null || jump.longitude == null
          ? []
          : [
              {
                latitude: jump.latitude,
                longitude: jump.longitude,
                label: jump.number,
                superfix: jumpCourseSuperfix(
                  {
                    canonicalSlug: coverageLevel.level.canonicalSlug,
                    number: jump.number,
                    name: jump.name,
                    latitude: jump.latitude,
                    longitude: jump.longitude,
                  },
                  labelCandidates,
                ),
                backgroundColor: colors.backgroundColor,
                textColor: colors.textColor,
              },
            ],
      );
    });
  }, [post.levels]);

  useEffect(() => {
    const element = mapRef.current;
    if (!element) return;
    const updateSize = () => {
      const width = Math.round(element.clientWidth);
      const height = Math.round(element.clientHeight);
      if (width === 0 || height === 0) return;
      setMapSize((current) =>
        current?.width === width && current.height === height ? current : { width, height },
      );
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!mapSize) return;
    let active = true;
    setImage(undefined);
    renderPostMapImage(points, mapSize)
      .then((result) => {
        if (active) setImage(result);
      })
      .catch(() => {
        if (active) setImage(null);
      });
    return () => {
      active = false;
    };
  }, [mapSize, points]);

  return (
    <div className="coverage-post-map" ref={mapRef}>
      {image ? (
        <img src={image} alt={`Map of judging post ${post.displayNumber}`} />
      ) : (
        <span>{image === undefined ? 'Loading map…' : 'Map unavailable'}</span>
      )}
    </div>
  );
}

function VolunteerOption({ volunteer }: { volunteer: Volunteer }) {
  const experienced = volunteer.experience === 'experienced' || volunteer.experience === 'expert';
  return (
    <option value={volunteer.id}>
      {volunteer.lastName}, {volunteer.firstName}
      {experienced
        ? ` (${volunteer.experience})`
        : volunteer.experience === 'apprentice'
          ? ' (Apprentice)'
          : ''}
    </option>
  );
}

function CoveragePill({ status }: { status: CoverageStatus }) {
  const label =
    status === 'full' ? 'Full coverage' : status === 'partial' ? 'Partial coverage' : 'Uncovered';
  const className =
    status === 'full' ? 'pill success' : status === 'partial' ? 'pill warn' : 'pill danger';
  return <span className={className}>{label}</span>;
}
